// Logique pure du parcours fiscal N−1 / N (testée sans DOM).
//
// Années : annee_revenus = année des revenus déclarés (N), annee_declaration = N + 1.
// Un document importé porte annee_document (année imprimée) et
// annee_revenus_document (année des revenus qu'il couvre).

export const PROFILS = ['premiere', 'deja_seul', 'ancien_rattache'];

export const defaultAnneeRevenus = (today = new Date()) => today.getFullYear() - 1;

export const yearsFor = (anneeRevenus) => ({
  anneeRevenus,
  anneeDeclaration: anneeRevenus + 1,
  anneeRevenusN1: anneeRevenus - 1,
});

const isEmpty = (v) => v === undefined || v === null || v === '';

// Une question est visible si toutes ses conditions showIf sont vraies ET si
// chaque question parente référencée est elle-même visible (multi-niveaux).
export const isQuestionVisible = (q, answers, byId, seen = new Set()) => {
  if (!q || !q.showIf) return true;
  if (seen.has(q.id)) return false; // garde-fou contre les cycles
  const nextSeen = new Set(seen).add(q.id);
  return Object.entries(q.showIf).every(([parentId, accepted]) => {
    const parent = byId[parentId];
    if (parent && !isQuestionVisible(parent, answers, byId, nextSeen)) return false;
    return Array.isArray(accepted) && accepted.includes(answers[parentId]);
  });
};

const indexById = (questions) => Object.fromEntries(questions.map((q) => [q.id, q]));

export const visibleQuestions = (questions, answers) => {
  const byId = indexById(questions);
  return questions.filter((q) => isQuestionVisible(q, answers, byId));
};

// Retire les réponses des questions devenues invisibles (ex. sous-question
// d'un parent repassé à « non ») avant l'envoi au backend.
export const pruneHiddenAnswers = (questions, answers) => {
  const byId = indexById(questions);
  const out = {};
  Object.entries(answers).forEach(([k, v]) => {
    const q = byId[k];
    if (!q || isQuestionVisible(q, answers, byId)) out[k] = v;
  });
  return out;
};

export const requiredAnswered = (questions, answers) =>
  visibleQuestions(questions, answers)
    .filter((q) => q.required)
    .every((q) => !isEmpty(answers[q.id]));

export const needsN1Import = (profil) => profil === 'deja_seul' || profil === 'ancien_rattache';

export const n1ImportOptional = (profil) => profil === 'ancien_rattache';

// Étapes du parcours selon le profil choisi.
export const flowSteps = (profil) => {
  const second = profil === 'premiere'
    ? { key: 'premiere', label: '1re déclaration' }
    : { key: 'n1', label: 'Avis N−1' };
  return [
    { key: 'profil', label: 'Profil' },
    ...(profil ? [second] : []),
    { key: 'questions', label: 'Questions' },
    { key: 'documents', label: 'Documents' },
    { key: 'comparaison', label: 'Comparaison' },
    { key: 'bilan', label: 'Bilan' },
  ];
};

// Les questions du profil sont posées à l'étape « Profil », le reste ensuite.
export const splitProfilQuestions = (questions) => ({
  profil: questions.filter((q) => q.section === 'profil'),
  others: questions.filter((q) => q.section !== 'profil'),
});

export const STATUS_META = {
  identique: { label: 'Identique', color: '#10b981', hint: "Comme l'an dernier" },
  nouveau: { label: 'Nouveau', color: '#3b82f6', hint: 'Nouveau cette année' },
  a_supprimer: { label: 'À supprimer', color: '#ef4444', hint: "Ne s'applique plus" },
  a_verifier: { label: 'À vérifier', color: '#f59e0b', hint: 'Montant ou situation changés' },
  document_manquant: { label: 'Justificatif manquant', color: '#a855f7', hint: 'Document à importer' },
};

export const statusMeta = (status) => STATUS_META[status] || { label: status || '—', color: '#a1a1a6', hint: '' };

// Libellé daté d'un document importé, sans jamais confondre les deux années.
export const documentYearLabel = (imp) => {
  if (!imp) return '';
  const doc = imp.annee_document;
  const rev = imp.annee_revenus_document;
  if (doc && rev && doc !== rev) return `Document ${doc} · revenus ${rev}`;
  if (rev) return `Revenus ${rev}`;
  if (doc) return `Document ${doc}`;
  return 'Année non détectée';
};

export const USAGE_LABELS = {
  reference_n1: 'Référence N−1',
  justificatif_n: 'Justificatif',
  archive: 'Année plus ancienne',
};

export const formatEuro = (v) =>
  typeof v === 'number' ? `${v.toLocaleString('fr-FR')} €` : '—';

// Champs saisis à l'étape « première déclaration » (aucun mot de passe).
export const emptyFirstDeclaration = () => ({
  nom: '',
  prenom: '',
  date_naissance: '',
  adresse_1er_janvier: '',
  situation_familiale: '',
  deja_rattache: null,
  annee_rattachement: '',
  employeurs: '',
  sources_revenus: [],
});

export const firstDeclarationPayload = (form, anneeRevenus) => ({
  annee_revenus: anneeRevenus,
  nom: form.nom.trim(),
  prenom: form.prenom.trim(),
  date_naissance: form.date_naissance || null,
  adresse_1er_janvier: form.adresse_1er_janvier.trim(),
  situation_familiale: form.situation_familiale || null,
  deja_rattache: form.deja_rattache,
  annee_rattachement: form.deja_rattache && form.annee_rattachement ? Number(form.annee_rattachement) : null,
  employeurs: form.employeurs.split(',').map((e) => e.trim()).filter(Boolean),
  sources_revenus: form.sources_revenus,
});
